import axios from "axios";
import { Base_URL } from "../url";

// ======================= PROJECT TYPE APIs =======================

/**
 * Get all project types by organisation ID
 * @param {number} orgId - Organisation ID
 * @returns {Promise<Array>} List of project types
 */
export const getProjectTypesByOrg = async (orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/ProjectConfiguration/types/by-org/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching project types:", error);
    throw error.response?.data || "Error fetching project types!";
  }
};

/**
 * Get project type by GUID and Organisation ID
 * @param {string} typeGuid - Project Type GUID
 * @param {number} orgId - Organisation ID
 * @returns {Promise<Object>} Project type data
 */
export const getProjectTypeByGuidAndOrg = async (typeGuid, orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/ProjectConfiguration/type/by-guid/${typeGuid}/org/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching project type by GUID:", error);
    throw error.response?.data || "Error fetching project type by GUID!";
  }
};

/**
 * Insert a new project type
 * @param {number} orgId - Organisation ID
 * @param {Object} typeData - Project type data
 * @returns {Promise<Object>} Insert result
 */
export const insertProjectType = async (orgId, typeData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/ProjectConfiguration/insert-type/org/${orgId}`,
      typeData,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error inserting project type:", error);
    throw error.response?.data || "Error inserting project type!";
  }
};

// ======================= PROJECT STATUS APIs =======================

/**
 * Get statuses by Type GUID and Organisation ID
 * @param {string} typeGuid - Project Type GUID
 * @param {number} orgId - Organisation ID
 * @returns {Promise<Array>} List of project statuses
 */
export const getStatusesByTypeAndOrg = async (typeGuid, orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/ProjectConfiguration/statuses/by-type/${typeGuid}/org/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching statuses:", error);
    throw error.response?.data || "Error fetching project statuses!";
  }
};

/**
 * Get project status by GUID and Organisation ID
 * @param {string} statusGuid - Project Status GUID
 * @param {number} orgId - Organisation ID
 * @returns {Promise<Object>} Project status data
 */
export const getStatusByGuidAndOrg = async (statusGuid, orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/ProjectConfiguration/status/by-guid/${statusGuid}/org/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching status by GUID:", error);
    throw error.response?.data || "Error fetching project status by GUID!";
  }
};

/**
 * Insert a new project status
 * @param {number} orgId - Organisation ID
 * @param {Object} statusData - Project status data
 * @returns {Promise<Object>} Insert result
 */
export const insertProjectStatus = async (orgId, statusData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/ProjectConfiguration/insert-status/org/${orgId}`,
      statusData,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error inserting project status:", error);
    throw error.response?.data || "Error inserting project status!";
  }
};

// ======================= PROJECT SUB-STATUS APIs =======================

/**
 * Get sub-statuses by Type GUID, Status GUID and Organisation ID
 * @param {string} typeGuid - Project Type GUID
 * @param {string} statusGuid - Project Status GUID
 * @param {number} orgId - Organisation ID
 * @returns {Promise<Array>} List of project sub-statuses
 */
export const getSubStatusesByTypeStatusAndOrg = async (
  typeGuid,
  statusGuid,
  orgId,
) => {
  try {
    const response = await axios.get(
      `${Base_URL}/ProjectConfiguration/substatuses/by-type/${typeGuid}/status/${statusGuid}/org/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching sub-statuses:", error);
    throw error.response?.data || "Error fetching project sub-statuses!";
  }
};

/**
 * Get project sub-status by GUID and Organisation ID
 * @param {string} subStatusGuid - Project Sub-Status GUID
 * @param {number} orgId - Organisation ID
 * @returns {Promise<Object>} Project sub-status data
 */
export const getSubStatusByGuidAndOrg = async (subStatusGuid, orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/ProjectConfiguration/substatus/by-guid/${subStatusGuid}/org/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching sub-status by GUID:", error);
    throw error.response?.data || "Error fetching project sub-status by GUID!";
  }
};

/**
 * Insert a new project sub-status
 * @param {number} orgId - Organisation ID
 * @param {Object} subStatusData - Project sub-status data
 * @returns {Promise<Object>} Insert result
 */
export const insertProjectSubStatus = async (orgId, subStatusData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/ProjectConfiguration/insert-substatus/org/${orgId}`,
      subStatusData,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error inserting project sub-status:", error);
    throw error.response?.data || "Error inserting project sub-status!";
  }
};

// ======================= MAPPING APIs =======================

/**
 * Insert mapping between Type, Status, and Sub-Status
 * @param {number} orgId - Organisation ID
 * @param {Object} mappingData - Mapping data
 * @returns {Promise<Object>} Insert result
 */
export const insertMapping = async (orgId, mappingData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/ProjectConfiguration/insert-mapping/org/${orgId}`,
      mappingData,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error inserting mapping:", error);
    throw error.response?.data || "Error inserting mapping!";
  }
};

/**
 * Bulk insert multiple mappings
 * @param {number} orgId - Organisation ID
 * @param {Array} mappings - Array of mapping data
 * @returns {Promise<Object>} Bulk insert result
 */
export const insertBulkMappings = async (orgId, mappings) => {
  try {
    const response = await axios.post(
      `${Base_URL}/ProjectConfiguration/insert-mappings/bulk/org/${orgId}`,
      mappings,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error inserting bulk mappings:", error);
    throw error.response?.data || "Error inserting bulk mappings!";
  }
};

// ======================= COMPLETE HIERARCHY API =======================

/**
 * Get complete hierarchy: Types with their Statuses and Sub-Statuses for an Organisation
 * @param {number} orgId - Organisation ID
 * @returns {Promise<Array>} Complete hierarchy data
 */
export const getCompleteHierarchy = async (orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/ProjectConfiguration/complete-hierarchy/org/${orgId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching complete hierarchy:", error);
    throw error.response?.data || "Error fetching complete hierarchy!";
  }
};

// ======================= INSERT ALL TABLES API =======================

/**
 * Insert data into all four tables (Type, Status, SubStatus, and Mapping) for an Organisation
 * @param {number} orgId - Organisation ID
 * @param {Object} allData - Object containing all data to insert
 * @returns {Promise<Object>} Insert result
 */
export const insertAllForOrganisation = async (orgId, allData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/ProjectConfiguration/insert-all-for-org/${orgId}`,
      allData,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error inserting all data:", error);
    throw error.response?.data || "Error inserting all data!";
  }
};

// ======================= UTILITY FUNCTIONS =======================

/**
 * Helper function to format project types for dropdown
 * @param {Array} types - Array of project types
 * @returns {Array} Formatted dropdown options
 */
export const formatProjectTypesForDropdown = (types) => {
  if (!types || !Array.isArray(types)) return [];
  return types.map((type) => ({
    value: type.projectTypeGuid,
    label: type.projectTypeDesc,
    id: type.projectTypeId,
    companycode: type.companycode,
    active: type.active,
  }));
};

/**
 * Helper function to format project statuses for dropdown
 * @param {Array} statuses - Array of project statuses
 * @returns {Array} Formatted dropdown options
 */
export const formatProjectStatusesForDropdown = (statuses) => {
  if (!statuses || !Array.isArray(statuses)) return [];
  return statuses.map((status) => ({
    value: status.projectStatusGuid,
    label: status.projectStatusDesc,
    id: status.projectStatusId,
    active: status.active,
  }));
};

/**
 * Helper function to format project sub-statuses for dropdown
 * @param {Array} subStatuses - Array of project sub-statuses
 * @returns {Array} Formatted dropdown options
 */
export const formatProjectSubStatusesForDropdown = (subStatuses) => {
  if (!subStatuses || !Array.isArray(subStatuses)) return [];
  return subStatuses.map((subStatus) => ({
    value: subStatus.projectSubStatusGuid,
    label: subStatus.projectSubStatusDesc,
    id: subStatus.projectSubStatusId,
    organisationId: subStatus.organisationId,
    active: subStatus.active,
  }));
};

/**
 * Get full configuration for a project type (including statuses and sub-statuses)
 * @param {number} orgId - Organisation ID
 * @param {string} typeGuid - Project Type GUID
 * @returns {Promise<Object>} Complete configuration for the project type
 */
export const getProjectTypeFullConfig = async (orgId, typeGuid) => {
  try {
    const statuses = await getStatusesByTypeAndOrg(typeGuid, orgId);

    const statusesWithSubStatuses = await Promise.all(
      statuses.map(async (status) => {
        const subStatuses = await getSubStatusesByTypeStatusAndOrg(
          typeGuid,
          status.projectStatusGuid,
          orgId,
        );
        return {
          ...status,
          subStatuses: subStatuses,
        };
      }),
    );

    return {
      typeGuid: typeGuid,
      statuses: statusesWithSubStatuses,
    };
  } catch (error) {
    console.error("Error fetching full project type config:", error);
    throw error;
  }
};

/**
 * Create a complete configuration (type, status, sub-status, and mapping) in one go
 * @param {number} orgId - Organisation ID
 * @param {Object} config - Configuration object
 * @returns {Promise<Object>} Creation result
 */
export const createCompleteConfiguration = async (orgId, config) => {
  try {
    const { typeData, statusData, subStatusData, mappingData } = config;

    // Create type
    const typeResult = await insertProjectType(orgId, typeData);
    if (!typeResult.success) throw new Error(typeResult.error);

    // Create status
    const statusResult = await insertProjectStatus(orgId, statusData);
    if (!statusResult.success) throw new Error(statusResult.error);

    // Create sub-status (if provided)
    let subStatusResult = null;
    if (subStatusData) {
      subStatusResult = await insertProjectSubStatus(orgId, subStatusData);
      if (!subStatusResult.success) throw new Error(subStatusResult.error);
    }

    // Create mapping
    const mappingRequest = {
      projectTypeGuid: typeResult.guid,
      projectStatusGuid: statusResult.guid,
      projectSubStatusGuid: subStatusResult?.guid || null,
      createdBy: config.createdBy || "system",
    };

    const mappingResult = await insertMapping(orgId, mappingRequest);
    if (!mappingResult.success) throw new Error(mappingResult.error);

    return {
      success: true,
      message: "Complete configuration created successfully",
      data: {
        type: typeResult,
        status: statusResult,
        subStatus: subStatusResult,
        mapping: mappingResult,
      },
    };
  } catch (error) {
    console.error("Error creating complete configuration:", error);
    throw error;
  }
};

// ======================= EXPORT ALL FUNCTIONS =======================

export default {
  // Project Type APIs
  getProjectTypesByOrg,
  getProjectTypeByGuidAndOrg,
  insertProjectType,

  // Project Status APIs
  getStatusesByTypeAndOrg,
  getStatusByGuidAndOrg,
  insertProjectStatus,

  // Project Sub-Status APIs
  getSubStatusesByTypeStatusAndOrg,
  getSubStatusByGuidAndOrg,
  insertProjectSubStatus,

  // Mapping APIs
  insertMapping,
  insertBulkMappings,

  // Complete Hierarchy API
  getCompleteHierarchy,

  // Insert All Tables API
  insertAllForOrganisation,

  // Utility Functions
  formatProjectTypesForDropdown,
  formatProjectStatusesForDropdown,
  formatProjectSubStatusesForDropdown,
  getProjectTypeFullConfig,
  createCompleteConfiguration,
};
